import { Inject } from '@angular/core';
import { isSupportedLanguage, Language } from '@core/enums/language';
import { LOCAL_STORAGE_TOKEN } from '@core/providers/storage';
import { CriticalErrorLogEntry } from '@models/critical-error-log-entry';
import { MILLISECONDS_PER_SECOND, SECONDS_PER_MINUTE } from '@utils/date';
import { logging } from '@utils/logging';

export const STORAGE_LANGUAGE_KEY = 'lang';
export const STORAGE_HIDDEN_WARNING_MESSAGE_KEY = 'hidden-warning-messageIds';

export const STORAGE_CRITICAL_ERROR_LOG_KEY = 'critical-error-log';

// Time limit for critical error log entries (1 minute)
const CRITICAL_ERROR_LOG_TIME_LIMIT_MS = SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;

/**
 * A service to interact with the browser's local storage.
 * Provides serialization, deserialization, type safety and allows for easier testing.
 */
export class Storage {
  constructor(@Inject(LOCAL_STORAGE_TOKEN) private readonly backingStorage: globalThis.Storage) {}

  getLanguage(): Language | null {
    const language: Language | null = this.getString(STORAGE_LANGUAGE_KEY) as Language;
    return language && isSupportedLanguage(language) ? language : null;
  }

  setLanguage(language: Language) {
    this.setString(STORAGE_LANGUAGE_KEY, language);
  }

  getHiddenWarningMessageIds(): string[] | null {
    return this.getObject<string[]>(STORAGE_HIDDEN_WARNING_MESSAGE_KEY);
  }

  setHiddenWarningMessageIds(ids: string[]) {
    this.setObject(STORAGE_HIDDEN_WARNING_MESSAGE_KEY, ids);
  }

  getCriticalErrorLog(): CriticalErrorLogEntry[] {
    return this.getObject<CriticalErrorLogEntry[]>(STORAGE_CRITICAL_ERROR_LOG_KEY) ?? [];
  }

  pushToCriticalErrorLog(error: unknown, url: string): CriticalErrorLogEntry[] {
    const log = this.getCriticalErrorLog();

    // Filter out old entries
    const now = Date.now();
    const filteredLog = log.filter(entry => now - entry.timestamp < CRITICAL_ERROR_LOG_TIME_LIMIT_MS);

    // Push new log entry
    filteredLog.push({ timestamp: now, message: String(error), url });

    // Store updated log
    this.setObject(STORAGE_CRITICAL_ERROR_LOG_KEY, filteredLog);

    return filteredLog;
  }

  clear() {
    try {
      this.backingStorage.clear();
    } catch (e) {
      logging.unknownError(e, { context: 'Storage.clear' });
    }
  }

  private setString(key: string, value: string): boolean {
    try {
      this.backingStorage.setItem(key, value);
      return true;
    } catch (e) {
      logging.unknownError(e, { context: `Storage.setString` });
      return false;
    }
  }

  private getString(key: string): string | null {
    try {
      return this.backingStorage.getItem(key);
    } catch (e) {
      logging.unknownError(e, { context: 'Storage.getString' });
      return null;
    }
  }

  private setObject<T>(key: string, value: T): boolean {
    try {
      this.backingStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      logging.unknownError(e, { context: `Storage.setObject` });
      return false;
    }
  }

  private getObject<T>(key: string): T | null {
    let data: string | null;
    try {
      data = this.backingStorage.getItem(key);
    } catch (e) {
      logging.unknownError(e, { context: 'Storage.getObject.getItem' });
      return null;
    }

    if (data === null) {
      return null;
    }

    try {
      return JSON.parse(data) as T;
    } catch (e) {
      logging.unknownError(e, { context: 'Storage.getObject', key });
      return null;
    }
  }
}
